// @ts-strict-ignore
import * as React from 'react';
import { SetupViewProps, SetupStep, SetupStepType } from '../../../../typescriptHelpers';
import { BranchesOutlined, CheckOutlined } from '@ant-design/icons';
import { IntegrationCapability, IntegrationType } from '../../../../types';
import {
    selectAppSettings,
    selectVCSIntegration,
    selectBuildSystemIntegration,
    selectOrgProviders,
    selectAuthCredentials,
} from '../../../../selectors/selectors';
import { antBlue, antGrey, runwayColors } from '../../../styles/globalStyles';
import Typography from 'antd/lib/typography';
import Text from 'antd/lib/typography/Text';
import Button from 'antd/lib/button';
import Collapse from 'antd/lib/collapse';
import Space from 'antd/lib/space';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../reducers/reducers';
import { getUpdated, getError, getSeparatedWorkflowFields } from '../../../../helpers';
import { AppSettingsUpdatable } from '../../../../actions/updatable';
import { tryUpdateAppSettings } from '../../../../actions/appSettings';
import {
    iconAndAltForIntegrationType,
    integrationUpdatableForIntegrationType,
} from '../../../../helpers/integrationHelpers';
import { ReleaseBranchModule } from './ReleaseBranchModule';
import { WorkflowModule } from './WorkflowModule';
import { WorkingBranchNameModule } from './WorkingBranchModule';
import { BranchVisualizationInfoViewComponent } from './BranchVisualizationInfoViewComponent';
import { InfoTip } from '../../shared';
import { globalMessage } from '../../../../utils/globalMessage';
import { useState } from 'react';
import { AppEntityRequest } from '../../../../actions/entities';
import * as API from '../../../../api';
import { getDisplayableWorkflowNames } from '../../../../helpers/displayHelpers';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { useForm } from 'antd/lib/form/Form';
import { useIntegrationSettingFields } from '@/utils/hooks/useIntegrationSettingFields';

const calculatePreferredActiveKeyNumber = ({
    workingBranch,
    workingBranchWorkflow,
    releaseBranch,
    RCWorkflow,
    productionWorkflow,
}: {
    workingBranch?: string;
    workingBranchWorkflow?: string;
    releaseBranch?: string;
    RCWorkflow?: string;
    productionWorkflow?: string;
}) => {
    if (workingBranch) {
        if (workingBranchWorkflow) {
            if (releaseBranch) {
                if (RCWorkflow) {
                    if (productionWorkflow) {
                        return null;
                    }
                    return 5;
                }
                return 4;
            }
            return 3;
        }
        return 2;
    }
    return 1;
};

const SetupViewComponent: React.FC<SetupViewProps> = (props) => {
    const dispatch = useDispatch();
    const [activeKey, setActiveKey] = React.useState('1');

    const workingBranch = useSelector((state: RootState) => selectAppSettings(state, props.appId).workingBranch);
    const stagingBranch = useSelector((state: RootState) => selectAppSettings(state, props.appId).stagingBranch);
    const prodBranch = useSelector((state: RootState) => selectAppSettings(state, props.appId).prodBranch);

    const setAdditionalBranchesIsLoading = useIsLoading(AppSettingsUpdatable.SetAdditionalBranches);
    const setAdditionalBranchesError = useError(AppSettingsUpdatable.SetAdditionalBranches);
    const setAdditionalBranchesSuccess = useUpdated(AppSettingsUpdatable.SetAdditionalBranches);

    const [workingBranchGuess, setWorkingBranchGuess] = useState('');
    const [isLoadingWorkingBranchGuess, setIsLoadingWorkingBranchGuess] = useState(false);

    const [releaseBranchGuess, setReleaseBranchGuess] = useState('');
    const [isLoadingReleaseBranchGuess, setIsLoadingReleaseBranchGuess] = useState(false);

    const buildSystemIntegration = useSelector((state: RootState) => selectBuildSystemIntegration(state, props.appId));

    const vcsIntegration = useSelector((state: RootState) => selectVCSIntegration(state, props.appId));

    const authCreds = useSelector((state: RootState) => selectAuthCredentials(state));

    React.useEffect(() => {
        if (setAdditionalBranchesSuccess) {
            globalMessage.success('Successfully saved branch!');
            props.onChange(false);
        }
    }, [setAdditionalBranchesSuccess]);

    React.useEffect(() => {
        if (setAdditionalBranchesError) {
            globalMessage.error(setAdditionalBranchesError);
        }
    }, [setAdditionalBranchesError]);

    const setAdditionalBranches = (values) => {
        dispatch(
            tryUpdateAppSettings(AppSettingsUpdatable.SetAdditionalBranches, {
                orgId: props.orgId,
                appId: props.appId,
                workingBranch: values.workingBranch ?? workingBranch,
                stagingBranch,
                prodBranch,
            }),
        );
    };

    const fetchGuessedWorkingBranch = () => {
        if (!workingBranch && !workingBranchGuess) {
            const request: AppEntityRequest = {
                orgId: props.orgId,
                appId: props.appId,
            };

            setIsLoadingWorkingBranchGuess(true);

            API.getSuggestedWorkingBranch(request, authCreds)
                .then((response) => {
                    setWorkingBranchGuess(response);
                })
                .catch(() => {
                    return;
                })
                .finally(() => {
                    setIsLoadingWorkingBranchGuess(false);
                });
        }
    };

    const fetchGuessedReleaseBranch = () => {
        if (!prodBranch && !releaseBranchGuess) {
            const request: AppEntityRequest = {
                orgId: props.orgId,
                appId: props.appId,
            };

            setIsLoadingReleaseBranchGuess(true);

            API.getSuggestedReleaseBranchPattern(request, authCreds)
                .then((response) => {
                    setReleaseBranchGuess(response);
                })
                .catch(() => {
                    return;
                })
                .finally(() => {
                    setIsLoadingReleaseBranchGuess(false);
                });
        }
    };

    React.useEffect(() => {
        if (activeKey === '1') {
            fetchGuessedWorkingBranch();
        }

        if (activeKey === '3') {
            fetchGuessedReleaseBranch();
        }
    }, [activeKey]);

    const releaseBranchPattern = useSelector((state: RootState) =>
        selectAppSettings(state, props.appId).releaseBranchPatterns?.find((pattern) => !pattern.isHotfix),
    );

    const integrationProviders = useSelector((state: RootState) => selectOrgProviders(state, props.orgId));
    const provider = integrationProviders.find((provider) => provider.id === buildSystemIntegration?.providerId);

    const [rcWorkflowForm] = useForm();
    const [devWorkflowForm] = useForm();
    const [prodWorkflowForm] = useForm();

    const { settingFields: buildSystemSettingFields } = useIntegrationSettingFields(
        props.orgId,
        props.appId,
        buildSystemIntegration?.installationId,
    );
    const { workingBranchWorkflowFields, RCWorkflowFields, productionWorkflowFields } = getSeparatedWorkflowFields(
        buildSystemSettingFields,
        false,
        buildSystemIntegration?.id,
    );

    const [vcsIntegrationIcon, vcsIntegrationIconAlt] =
        vcsIntegration ? iconAndAltForIntegrationType(vcsIntegration.id) : [];
    const [buildSystemIntegrationIcon, buildSystemIntegrationIconAlt] =
        buildSystemIntegration ? iconAndAltForIntegrationType(buildSystemIntegration.id) : [];

    // get displayable names
    const { workingBranchWorkflow, RCWorkflow, productionWorkflow } = getDisplayableWorkflowNames({
        buildSystemIntegration,
        provider,
    });

    const integrationSetupType =
        buildSystemIntegration && integrationUpdatableForIntegrationType(buildSystemIntegration.id);

    const updateIntegrationSuccess = useSelector((state: RootState) =>
        getUpdated(integrationSetupType, state, buildSystemIntegration?.installationId),
    );
    const updateIntegrationError = useSelector((state: RootState) =>
        getError(integrationSetupType, state, buildSystemIntegration?.installationId),
    );

    React.useEffect(() => {
        if (updateIntegrationSuccess) {
            props.onChange(false);
            globalMessage.success('Workflow updated!');
        }
    }, [updateIntegrationSuccess]);

    React.useEffect(() => {
        if (updateIntegrationError) {
            globalMessage.error(updateIntegrationError);
        }
    }, [updateIntegrationError]);

    const preferredActiveKeyNumber = calculatePreferredActiveKeyNumber({
        workingBranch,
        releaseBranch: releaseBranchPattern?.pattern,
        workingBranchWorkflow,
        RCWorkflow,
        productionWorkflow,
    });

    React.useEffect(() => {
        setActiveKey(preferredActiveKeyNumber?.toString());
    }, [preferredActiveKeyNumber]);

    const doesSupportSelectingWorkflows =
        buildSystemIntegration?.id !== IntegrationType.TravisCI &&
        buildSystemIntegration?.id !== IntegrationType.CustomCI;

    const buildSystemIcon = buildSystemIntegrationIcon && (
        <img
            src={buildSystemIntegrationIcon}
            alt={buildSystemIntegrationIconAlt}
            width={25}
            height={25}
            style={{ marginRight: 8 }}
        />
    );

    const vcsIcon = !!vcsIntegrationIcon && (
        <img src={vcsIntegrationIcon} alt={vcsIntegrationIconAlt} width={25} height={25} style={{ marginRight: 8 }} />
    );

    const connectCIButton = (
        <Button
            type="link"
            onClick={props.displayRelatedIntegrationsSetup}
            style={{
                height: 0,
                padding: 0,
            }}
        >
            Connect to CI
        </Button>
    );

    const isActiveKey = activeKey === '1';

    return (
        <>
            <Collapse
                defaultActiveKey={[activeKey]}
                activeKey={activeKey ? [activeKey] : []}
                bordered={false}
                expandIcon={() => null}
                accordion
                destroyInactivePanel={false}
                className="setup-steps-accordion"
                onChange={(activeKey) => {
                    setActiveKey(activeKey as string);
                }}
            >
                <Collapse.Panel
                    key="1"
                    extra={
                        activeKey !== '1' &&
                        workingBranch && (
                            <>
                                {vcsIcon}
                                <Text code>{workingBranch}</Text>
                                <CheckOutlined style={{ marginLeft: 8, color: runwayColors.statusGreen }} />
                            </>
                        )
                    }
                    header={
                        <Space direction="vertical" size={4}>
                            <Typography.Text
                                style={{
                                    color: activeKey === '1' ? antGrey.grey8 : antBlue.blue5,
                                    fontSize: 16,
                                    fontWeight: activeKey === '1' ? 400 : 600,
                                }}
                            >
                                {activeKey === '1' ? 'What’s the name of your working branch?' : 'Working branch'}
                            </Typography.Text>
                            {isActiveKey && (
                                <Typography.Text type="secondary">
                                    {`Your team's main development branch, e.g. 'development' or 'main'`}
                                </Typography.Text>
                            )}
                        </Space>
                    }
                >
                    <WorkingBranchNameModule
                        {...props}
                        workingBranch={workingBranch}
                        workingBranchGuess={workingBranchGuess}
                        isLoadingWorkingBranchGuess={isLoadingWorkingBranchGuess}
                        setAdditionalBranches={setAdditionalBranches}
                        setAdditionalBranchesIsLoading={setAdditionalBranchesIsLoading}
                    />
                </Collapse.Panel>

                {doesSupportSelectingWorkflows && (
                    <Collapse.Panel
                        key="2"
                        collapsible={buildSystemIntegration ? undefined : 'disabled'}
                        extra={
                            !buildSystemIntegration ?
                                <>{connectCIButton}</>
                            :   activeKey !== '2' &&
                                workingBranchWorkflow && (
                                    <>
                                        {buildSystemIcon}
                                        <Text code>{workingBranchWorkflow}</Text>
                                        <CheckOutlined style={{ marginLeft: 8, color: runwayColors.statusGreen }} />
                                    </>
                                )
                        }
                        header={
                            <>
                                <Typography.Text
                                    style={{
                                        color: activeKey === '2' ? antGrey.grey8 : antBlue.blue5,
                                        fontSize: 16,
                                        fontWeight: activeKey === '2' ? 400 : 600,
                                    }}
                                >
                                    {activeKey === '2' ?
                                        'What’s the name of the development workflow that builds off your working branch?'
                                    :   'Working branch workflow'}
                                </Typography.Text>
                            </>
                        }
                    >
                        <WorkflowModule
                            {...props}
                            form={devWorkflowForm}
                            fields={workingBranchWorkflowFields}
                            integration={buildSystemIntegration}
                            integrationSetupType={integrationSetupType}
                        />
                    </Collapse.Panel>
                )}

                <Collapse.Panel
                    key="3"
                    extra={
                        activeKey !== '3' &&
                        releaseBranchPattern && (
                            <>
                                {vcsIcon}
                                <Text code>{releaseBranchPattern.pattern}</Text>
                                <CheckOutlined style={{ marginLeft: 8, color: runwayColors.statusGreen }} />
                            </>
                        )
                    }
                    header={
                        <>
                            <Typography.Text
                                style={{
                                    color: activeKey === '3' ? antGrey.grey8 : runwayColors.statusGreen,
                                    fontSize: 16,
                                    fontWeight: activeKey === '3' ? 400 : 600,
                                }}
                            >
                                {activeKey === '3' ?
                                    'What’s the name or pattern of your release branch?'
                                :   'Release branch'}
                            </Typography.Text>
                        </>
                    }
                >
                    <ReleaseBranchModule
                        {...props}
                        vcsIntegration={vcsIntegration}
                        workingBranch={workingBranch}
                        releaseBranchPattern={releaseBranchPattern}
                        releaseBranchPatternGuess={releaseBranchGuess}
                        isLoadingReleaseBranchPatternGuess={isLoadingReleaseBranchGuess}
                    />
                </Collapse.Panel>

                <Collapse.Panel
                    key="4"
                    collapsible={buildSystemIntegration ? undefined : 'disabled'}
                    extra={
                        !buildSystemIntegration ?
                            <>{connectCIButton}</>
                        :   activeKey !== '4' &&
                            RCWorkflow && (
                                <>
                                    {buildSystemIcon}
                                    <Text code>{RCWorkflow}</Text>
                                    <CheckOutlined style={{ marginLeft: 8, color: runwayColors.statusGreen }} />
                                </>
                            )
                    }
                    header={
                        <>
                            <Typography.Text
                                style={{
                                    color: activeKey === '4' ? antGrey.grey8 : runwayColors.statusGreen,
                                    fontSize: 16,
                                    fontWeight: activeKey === '4' ? 400 : 600,
                                }}
                            >
                                {activeKey === '4' ?
                                    'What’s the name of your Release Candidate workflow?'
                                :   'RC workflow'}
                            </Typography.Text>
                        </>
                    }
                >
                    <WorkflowModule
                        {...props}
                        form={rcWorkflowForm}
                        fields={RCWorkflowFields}
                        integration={buildSystemIntegration}
                        integrationSetupType={integrationSetupType}
                    />
                </Collapse.Panel>

                {doesSupportSelectingWorkflows && (
                    <Collapse.Panel
                        key="5"
                        collapsible={buildSystemIntegration ? undefined : 'disabled'}
                        extra={
                            !buildSystemIntegration ?
                                <>{connectCIButton}</>
                            :   activeKey !== '5' &&
                                productionWorkflow && (
                                    <>
                                        {buildSystemIcon}
                                        <Text code>{productionWorkflow}</Text>
                                        <CheckOutlined style={{ marginLeft: 8, color: runwayColors.statusGreen }} />
                                    </>
                                )
                        }
                        header={
                            <>
                                <Typography.Text
                                    style={{
                                        color: activeKey === '5' ? antGrey.grey8 : runwayColors.statusGreen,
                                        fontSize: 16,
                                        fontWeight: activeKey === '5' ? 400 : 600,
                                    }}
                                >
                                    {activeKey === '5' ?
                                        'Do you use a separate production workflow?'
                                    :   'Production workflow'}
                                </Typography.Text>
                            </>
                        }
                    >
                        <WorkflowModule
                            {...props}
                            form={prodWorkflowForm}
                            fields={productionWorkflowFields}
                            integration={buildSystemIntegration}
                            integrationSetupType={integrationSetupType}
                        />
                    </Collapse.Panel>
                )}
            </Collapse>
            <div style={{ marginTop: 40, display: 'flex', justifyContent: 'center' }}>
                <Space direction="vertical" align="center" size={30}>
                    <Typography.Text
                        style={{
                            color: antGrey.grey8,
                            fontSize: 16,
                        }}
                    >
                        Review the above, and hit <strong>Next</strong> if everything looks correct!
                    </Typography.Text>
                    <div style={{ maxWidth: 600, width: '100%' }}>
                        <InfoTip>
                            If your team uses a different branch to generate your production or staging builds, or if
                            your workflow requires specific build variables, you can configure a more advanced builds
                            and branches setup later.
                        </InfoTip>
                    </div>
                </Space>
            </div>
        </>
    );
};

export const BranchingStrategyStep: SetupStep = {
    title: 'Tell us about your branching and build workflows.',
    description: () => {
        return 'Your team’s branching and build workflow is one of the most important parts of the release process — and key to getting up and running in Runway.';
    },
    icon: () => BranchesOutlined,
    SetupViewComponent,
    InfoViewComponent: BranchVisualizationInfoViewComponent,
    listTitle: 'Branches and builds',
    listDescription: 'Tell us about your workflow setup.',
    integrationsRecommendationBannerData: {
        title: () => {
            return 'Connect your version control system and CI provider';
        },
        description: 'Set up is much easier if you connect these up front (you’ll need to do it eventually!)',
        actionText: 'OK, let’s do it',
        type: SetupStepType.BranchingStrategy,
        integrationCapabilities: [IntegrationCapability.VersionControl, IntegrationCapability.BuildSystem],
    },
    isCompleteSelector: (root, { appId, orgId }) => {
        const releaseBranchPattern = selectAppSettings(root, appId).releaseBranchPatterns?.find(
            (pattern) => !pattern.isHotfix,
        );

        const buildSystemIntegration = selectBuildSystemIntegration(root, appId);

        const integrationProviders = selectOrgProviders(root, orgId);

        const provider = integrationProviders.find((provider) => provider.id === buildSystemIntegration?.providerId);

        const workflowNames =
            provider ?
                getDisplayableWorkflowNames({
                    buildSystemIntegration,
                    provider,
                })
            :   null;

        const workingBranch = selectAppSettings(root, appId).workingBranch;

        return !!releaseBranchPattern && !!workingBranch && !!workflowNames?.RCWorkflow;
    },
};
